import { createFileRoute } from "@tanstack/react-router"
import { getProfileBySlug } from "~/lib/server-fns"
import { SECTION_LABELS } from "~/data/profiles"
import { RichText } from "~/components/RichText"
import { KeyModelContent } from "~/components/report/KeyModelContent"
import { ProfileContent } from "~/components/report/ProfileContent"
import { ProfileInvitations } from "~/components/report/ProfileInvitations"

export const Route = createFileRoute("/profile/$slug")({
  component: ProfilePage,
  validateSearch: (search: Record<string, unknown>) => ({
    preview: (search.preview === "true" || search.preview === true) || undefined,
  }),
  loaderDeps: ({ search }) => ({ preview: !!search.preview }),
  loader: async ({ params, deps }) => {
    const profile = await getProfileBySlug({ data: { slug: params.slug, preview: deps.preview } })
    return { profile }
  },
  errorComponent: () => (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl text-core-orange mb-4">Profile Not Found</h1>
        <p className="text-white/60 mb-6">This profile does not exist.</p>
        <a
          href="/"
          className="inline-block bg-core-orange text-white font-bold py-2 px-6 rounded-full hover:opacity-90 transition-opacity"
        >
          Take the Assessment
        </a>
      </div>
    </div>
  ),
})

function ProfilePage() {
  const { profile } = Route.useLoaderData()

  return (
    <div className="flex-1">
      <div className="max-w-[1080px] mx-auto">
        {/* Profile Header */}
        <section className="flex flex-col items-center gap-5 px-4 sm:px-[60px] py-8">
          <h2 className="text-4xl text-core-orange tracking-widest text-center">
            {SECTION_LABELS.coreProfile}
          </h2>
          <p className="text-white/60 text-center">
            {profile.tagline}
          </p>
          <h3 className="text-3xl text-white text-center">
            {profile.name.toUpperCase()}
          </h3>
        </section>

        {/* Opening Quote */}
        <section className="px-4 sm:px-[60px] py-6">
          <RichText value={profile.openingQuote} className="text-white/70 italic leading-relaxed text-center" />
        </section>

        {/* The Model */}
        {profile.keyModel.content && (
          <section className="px-4 sm:px-[60px] py-10 sm:py-12">
            <div className="w-full h-px bg-white/10 mb-6" />
            <h3 className="text-2xl text-core-orange tracking-wider mb-6">
              {SECTION_LABELS.keyModel}
            </h3>
            <KeyModelContent content={profile.keyModel.content} className="text-white/80 leading-relaxed" />
          </section>
        )}

        {/* Profile Content */}
        <div className="px-4 sm:px-[60px]">
          <ProfileContent profile={profile} />
          <ProfileInvitations profile={profile} />
        </div>

        {/* CTA */}
        <section className="px-4 sm:px-[60px] py-12 flex justify-center">
          <a
            href="/"
            className="inline-block bg-core-orange text-white font-bold py-3 px-10 rounded-full text-lg hover:opacity-90 transition-opacity"
          >
            Take the Assessment
          </a>
        </section>
      </div>
    </div>
  )
}
